import { useCallback, useMemo, useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import Container from "@mui/material/Container";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Collapse, Progress, Table, Input } from "antd";
import type { TableProps } from "antd";
import dayjs from "dayjs";

import {
  Maybe,
  MentorshipProgramProgress,
  WorkspaceUsers,
} from "../../graphql/generated/graphql";
import {
  useGetMentorshipProgramByIdQuery,
  GetMentorshipProgramByIdQuery,
} from "../../graphql/queries/getMentorshipProgramById.generated";
import { useGetMentorshipParticipantsByWorkspaceIdQuery } from "../../graphql/queries/getMentorshipParticipantsByWorkspaceId.generated";
import { useGetMentorshipParticipantsByWorkspaceLocationIdQuery } from "../../graphql/queries/getMentorshipParticipantsByWorkspaceLocationId.generated";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import { MatchingMethod } from "../../constants/input-values";

type ColumnsType<T extends object> = TableProps<T>["columns"];

// Extract the module type from the query response
type MentorshipProgramModule = NonNullable<
  GetMentorshipProgramByIdQuery["mentorshipProgram_by_pk"]
>["mentorshipProgramModules"][0];

interface RelationshipsType {
  type: string | undefined;
  id: string | number;
  mentor:
    | null
    | {
        __typename?: "users" | undefined;
        id: string;
        fullName?: string | null | undefined;
        profilePhotoLink?: string | null | undefined;
        menteeEnabled?: boolean | null | undefined;
        mentorEnabled?: boolean | null | undefined;
      }
    | undefined;
  mentee:
    | null
    | {
        __typename?: "users" | undefined;
        id: string;
        fullName?: string | null | undefined;
        profilePhotoLink?: string | null | undefined;
        menteeEnabled?: boolean | null | undefined;
        mentorEnabled?: boolean | null | undefined;
      }
    | undefined;
  status: string;
  matchDate: string;
  completionPercent: number;
}

interface ParticipantsType {
  id: string;
  name: string | null | undefined;
  profilePhotoLink: string | null | undefined;
  mentorEnabled?: Maybe<boolean> | undefined;
  menteeEnabled?: Maybe<boolean> | undefined;
  specialty: string | undefined;
  location: string | undefined;
  email: string;
  checkins: string;
}

const participantsColumns: ColumnsType<ParticipantsType> = [
  {
    title: "Name",
    key: "name",
    dataIndex: "name",
    render: (field, record, index) => {
      const loggedUserIsBoth = record?.mentorEnabled && record?.menteeEnabled;
      const loggedUserIsMentor = record?.mentorEnabled;
      const loggedUserIsMentee = record?.menteeEnabled;

      let imgClassname = "profile-img-wrapper";
      let imgPath = process.env.PUBLIC_URL + "/assets/images/mentor-filter.svg";

      switch (true) {
        case loggedUserIsBoth:
        case loggedUserIsMentee:
          imgClassname = "profile-img-wrapper orange";
          imgPath = process.env.PUBLIC_URL + "/assets/images/mentee-filter.svg";
          break;
        case loggedUserIsMentor:
          imgClassname = "profile-img-wrapper";
          imgPath = process.env.PUBLIC_URL + "/assets/images/mentor-filter.svg";
          break;
        default:
          imgClassname = "profile-img-wrapper";
          imgPath = process.env.PUBLIC_URL + "/assets/images/mentor-filter.svg";
          break;
      }

      if (field) {
        return (
          <div className="mentorship-details-user-row">
            <div className={imgClassname}>
              <img
                src={record?.profilePhotoLink || imgPath}
                alt="mentor profile"
                className="mentorship-details-profile-img"
              />
            </div>
            <span className="mentorship-details-table-text">
              {field || "N/A"}
            </span>
          </div>
        );
      }
      return <span className="mentorship-details-table-text">N/A</span>;
    },
  },
  {
    title: "Specialty",
    key: "specialty",
    dataIndex: "specialty",
    render: (item) => {
      if (item) {
        return <span className="mentorship-details-table-text">{item}</span>;
      }
    },
  },
  {
    title: "Location",
    key: "location",
    dataIndex: "location",
    render: (item) => {
      if (item) {
        return <span className="mentorship-details-table-text">{item}</span>;
      }
    },
  },
  {
    title: "Email",
    key: "email",
    dataIndex: "email",
    render: (item) => {
      if (item) {
        return <span className="mentorship-details-table-text">{item}</span>;
      }
    },
  },
  {
    title: "Check-Ins",
    key: "checkins",
    dataIndex: "checkins",
    render: (field, record, index) => {
      // if (record.type === "mentorship") {
      //   return (
      //     <span className="mentorship-details-table-status complete">
      //       {field}
      //     </span>
      //   );
      // }

      return <span className="mentorship-details-table-text">{field}</span>;
    },
  },
];

export default function MentorshipProgramDetails() {
  const { loggedUserData } = useAuth();
  const params = useParams();
  const location = useLocation();
  const programId = params.programId ? params.programId : "";
  const openRelationship = params.openRelationship
    ? params.openRelationship
    : "1";

  const navigate = useNavigate();

  const [workspaceId, setWorkspaceId] = useState<number>(0);

  const [programWorkspaceId, setProgramWorkspaceId] = useState<number>(0);
  const [programWorkspaceLocationId, setProgramWorkspaceLocationId] =
    useState<number>(0);
  const [programCompletedCount, setProgramCompletedCount] = useState<number>(0);
  const [programCompletedPercent, setProgramCompletedPercent] =
    useState<number>(0);
  const [programMatchingMethod, setProgramMatchingMethod] =
    useState<string>("");
  const [mentorLabel, setMentorLabel] = useState<string>("");
  const [menteeLabel, setMenteeLabel] = useState<string>("");
  const [mentorFilterEnabled, setMentorFilterEnabled] =
    useState<boolean>(false);
  const [menteeFilterEnabled, setMenteeFilterEnabled] =
    useState<boolean>(false);
  const [searchParticipantsText, setSearchParticipantsText] =
    useState<string>("");
  const [searchRelationshipsText, setSearchRelationshipsText] =
    useState<string>("");
  const [activeKeys, setActiveKeys] = useState<string[]>([
    "curriculum-overview-content",
  ]);

  //COLUMN SECTION
  const columns = useMemo(() => {
    const columnsData: ColumnsType<MentorshipProgramProgress> = [
      {
        title: mentorLabel || "Mentor",
        key: "Mentor",
        render: (field, record, index) => {
          return (
            <div className="mentorship-details-user-row">
              <div className={"profile-img-wrapper"}>
                <img
                  src={
                    record.mentorship.mentor?.profilePhotoLink ||
                    process.env.PUBLIC_URL + "/assets/images/mentor-filter.svg"
                  }
                  alt="mentor-profile-pic"
                  className="mentorship-details-profile-img"
                />
              </div>
              <span className="mentorship-details-table-text">
                {record.mentorship.mentor?.fullName}
              </span>
            </div>
          );
        },
      },
      {
        title: menteeLabel || "Mentee",
        key: "Mentee",
        render: (field, record, index) => {
          return (
            <div className="mentorship-details-user-row">
              <div className={"profile-img-wrapper orange"}>
                <img
                  src={
                    record.mentorship.mentee?.profilePhotoLink ||
                    process.env.PUBLIC_URL + "/assets/images/mentee-filter.svg"
                  }
                  alt="mentee-profile-pic"
                  className="mentorship-details-profile-img"
                />
              </div>
              <span className="mentorship-details-table-text">
                {record.mentorship.mentee?.fullName}
              </span>
            </div>
          );
        },
      },
      {
        title: "Start Date",
        key: "startDate",
        render: (field, record, index) => {
          if (field.startDate) {
            return (
              <span className="mentorship-details-table-text">
                {dayjs(field.startDate).format("MM/DD/YYYY")}
              </span>
            );
          } else {
            return <span className="mentorship-details-table-text">{"-"}</span>;
          }
        },
      },
      {
        title: "Due Date",
        key: "dueDate",
        render: (field, record, index) => {
          if (field.dueDate) {
            return (
              <span className="mentorship-details-table-text">
                {dayjs(field.dueDate).format("MM/DD/YYYY")}
              </span>
            );
          } else {
            return <span className="mentorship-details-table-text">{"-"}</span>;
          }
        },
      },
      {
        title: "Status",
        key: "Status",
        render: (field, record, index) => {
          if (record.completed) {
            return (
              <span className="mentorship-details-table-status complete">
                {"COMPLETE"}
              </span>
            );
          }

          if (record.messageId && record.completed === false) {
            return (
              <span className="mentorship-details-table-status progress">
                {"IN PROGRESS"}
              </span>
            );
          }

          return (
            <span className="mentorship-details-table-status not-started">
              {"NOT STARTED"}
            </span>
          );
        },
      },
      {
        title: "Completed On",
        key: "reportedAsCompletedAt",
        dataIndex: "reportedAsCompletedAt",
        render: (item, record) => {
          const completedDate = item || record.completedAt;

          if (record.completed && completedDate) {
            return (
              <span className="mentorship-details-table-text">
                {dayjs(completedDate).format("MM/DD/YYYY")}
              </span>
            );
          } else {
            return <span className="mentorship-details-table-text">{"-"}</span>;
          }
        },
      },
      {
        title: "Meeting Type",
        key: "Meeting Type",
        render: (field, record, index) => {
          if (record.completed) {
            if (record.reportedAsCompletedAt) {
              return (
                <span className="mentorship-details-table-text">
                  {"Offline"}
                </span>
              );
            }

            if (record.messageId) {
              return (
                <span className="mentorship-details-table-text">
                  {"In-App"}
                </span>
              );
            }
          } else {
            if (record.messageId) {
              return (
                <span className="mentorship-details-table-text">
                  {"In-App"}
                </span>
              );
            } else {
              return (
                <span className="mentorship-details-table-text">{"-"}</span>
              );
            }
          }
        },
      },
    ];
    return columnsData;
  }, [mentorLabel, menteeLabel]);

  const relationshipsColumns = useMemo(() => {
    const columnsData: ColumnsType<RelationshipsType> = [
      {
        title: mentorLabel || "Mentor",
        key: "mentor",
        dataIndex: "mentor",
        render: (item) => {
          if (item) {
            return (
              <div className="mentorship-details-user-row">
                <div className={"profile-img-wrapper"}>
                  <img
                    src={
                      item?.profilePhotoLink ||
                      process.env.PUBLIC_URL +
                        "/assets/images/mentor-filter.svg"
                    }
                    alt="mentor profile"
                    className="mentorship-details-profile-img"
                  />
                </div>
                <span className="mentorship-details-table-text">
                  {item.fullName || "N/A"}
                </span>
              </div>
            );
          }
          return <span className="mentorship-details-table-text">N/A</span>;
        },
      },
      {
        title: menteeLabel || "Mentee",
        key: "mentee",
        dataIndex: "mentee",
        render: (item) => {
          if (item) {
            return (
              <div className="mentorship-details-user-row">
                <div className={"profile-img-wrapper orange"}>
                  <img
                    src={
                      item?.profilePhotoLink ||
                      process.env.PUBLIC_URL +
                        "/assets/images/mentee-filter.svg"
                    }
                    alt="mentee profile"
                    className="mentorship-details-profile-img"
                  />
                </div>
                <span className="mentorship-details-table-text">
                  {item.fullName || "N/A"}
                </span>
              </div>
            );
          }
          return <span className="mentorship-details-table-text">N/A</span>;
        },
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: (field, record, index) => {
          if (record.type === "mentorship") {
            return (
              <span className="mentorship-details-table-status complete">
                {field}
              </span>
            );
          }

          return (
            <span className="mentorship-details-table-status not-started">
              {field}
            </span>
          );
        },
      },
      {
        title: "Match Date",
        key: "matchDate",
        dataIndex: "matchDate",
        render: (item) => {
          if (item) {
            return (
              <span className="mentorship-details-table-text">{item}</span>
            );
          }
        },
      },
      {
        title: "Completion",
        key: "completionPercent",
        dataIndex: "completionPercent",
        render: (field, record, index) => {
          return (
            <div className="relationships-completion-row">
              <span className="relationships-completion-text">{`${field}%`}</span>
              {/* NOTE: Progress component must be imported for this to work */}
              <Progress
                percent={field}
                showInfo={false}
                strokeColor={
                  "linear-gradient(108deg, #FB7798 10.92%, #EC76FF 164.77%)"
                }
                trailColor={"#FFE0EE"}
              />
            </div>
          );
        },
      },
    ];
    return columnsData;
  }, [mentorLabel, menteeLabel]);

  useEffect(() => {
    if (loggedUserData?.workspaces?.length) {
      setWorkspaceId(loggedUserData.workspaces[0].workspace.id);
    }
  }, [loggedUserData]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const sectionToOpen = query.get("sectionKey");

    if (sectionToOpen === "relationships") {
      setActiveKeys(["relationship-content"]);
    }
  }, [location.search]);

  const handleOnCompletedGetMentorshipProgramById = useCallback(
    (data: GetMentorshipProgramByIdQuery) => {
      console.log("Get MentorshipProgram ById data: ", data);
      const {
        workspaceId,
        workspaceLocationId,
        mentorshipProgramModules,
        matchingMethod,
        mentorLabel,
        menteeLabel,
      } = data.mentorshipProgram_by_pk || {};
      if (workspaceId) {
        setProgramWorkspaceId(workspaceId);
      }

      if (workspaceLocationId) {
        setProgramWorkspaceLocationId(workspaceLocationId);
      }

      if (matchingMethod) {
        setProgramMatchingMethod(matchingMethod);
      }

      if (mentorLabel) {
        setMentorLabel(mentorLabel);
      }

      if (menteeLabel) {
        setMenteeLabel(menteeLabel);
      }

      const progresses = (mentorshipProgramModules ?? []).flatMap(
        (m) => m.mentorshipProgramProgresses ?? []
      );

      const total = progresses.length;
      const completedCount = progresses.filter((p) => p.completed).length;
      const completionPercent = total
        ? Math.round((completedCount * 100) / total)
        : 0;

      setProgramCompletedCount(completedCount);
      setProgramCompletedPercent(completionPercent);
    },
    []
  );

  const handleOnErrorsGetMentorshipProgramById = useCallback(
    (error: ApolloError) => {
      console.log("Get MentorshipProgram ById error: ", error);
    },
    []
  );

  const { data: mentorshipProgramData, loading: mentorshipProgramLoading } =
    useGetMentorshipProgramByIdQuery({
      skip: !programId,
      variables: {
        id: Number(programId),
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetMentorshipProgramById,
      onError: handleOnErrorsGetMentorshipProgramById,
    });

  const handleOnCompletedGetMentorshipParticipantsByWorkspaceId = useCallback(
    (data: any) => {
      console.log("Get Mentorship Participants By WorkspaceId data: ", data);
    },
    []
  );

  const handleOnErrorsGetMentorshipParticipantsByWorkspaceId = useCallback(
    (error: ApolloError) => {
      console.log("Get Mentorship Participants By WorkspaceId error: ", error);
    },
    []
  );

  // total # of eligible users. If the mentorshipProgram is available to all users in the workspace, this is a count of users in that workspace with menteeEnabled = TRUE, mentorEnabled = TRUE, or both.
  const {
    data: mentorshipParticipantsByWorkspaceIdData,
    loading: mentorshipParticipantsByWorkspaceIdLoading,
  } = useGetMentorshipParticipantsByWorkspaceIdQuery({
    skip: !programWorkspaceId || !!programWorkspaceLocationId,
    variables: {
      workspaceId: programWorkspaceId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedGetMentorshipParticipantsByWorkspaceId,
    onError: handleOnErrorsGetMentorshipParticipantsByWorkspaceId,
  });

  const handleOnCompletedGetMentorshipParticipantsByWorkspaceLocationId =
    useCallback((data: any) => {
      console.log(
        "Get Mentorship Participants By WorkspaceLocationId data: ",
        data
      );
    }, []);

  const handleOnErrorsGetMentorshipParticipantsByWorkspaceLocationId =
    useCallback((error: ApolloError) => {
      console.log(
        "Get Mentorship Participants By WorkspaceLocationId  error: ",
        error
      );
    }, []);

  // If the mentorshipProgram is available to users at a single location, this is a count of users with that workspaceLocationId with menteeEnabled = TRUE, mentorEnabled = TRUE, or both.
  const {
    data: mentorshipParticipantsByWorkspaceLocationIdData,
    loading: mentorshipParticipantsByWorkspaceLocationIdLoading,
  } = useGetMentorshipParticipantsByWorkspaceLocationIdQuery({
    skip: !programWorkspaceLocationId,
    variables: {
      workspaceId: programWorkspaceId,
      workspaceLocationId: programWorkspaceLocationId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted:
      handleOnCompletedGetMentorshipParticipantsByWorkspaceLocationId,
    onError: handleOnErrorsGetMentorshipParticipantsByWorkspaceLocationId,
  });

  const isLoading =
    mentorshipProgramLoading ||
    mentorshipParticipantsByWorkspaceIdLoading ||
    mentorshipParticipantsByWorkspaceLocationIdLoading;

  const participantsValue = useMemo(() => {
    if (programWorkspaceLocationId) {
      return mentorshipParticipantsByWorkspaceLocationIdData?.workspaceUsers
        .length;
    }

    if (programWorkspaceId) {
      return mentorshipParticipantsByWorkspaceIdData?.workspaceUsers.length;
    }

    return 0;
  }, [
    programWorkspaceId,
    programWorkspaceLocationId,
    mentorshipParticipantsByWorkspaceLocationIdData,
    mentorshipParticipantsByWorkspaceIdData,
  ]);

  const matchedValue = useMemo(() => {
    const mentorshipProgram = mentorshipProgramData?.mentorshipProgram_by_pk;

    if (mentorshipProgram?.mentorships) {
      const totals: Set<string> = mentorshipProgram.mentorships.reduce(
        (individualUsersIds: Set<string>, currentValue: any) => {
          individualUsersIds.add(currentValue.menteeId);
          individualUsersIds.add(currentValue.mentorId);
          return individualUsersIds;
        },
        new Set()
      );

      const mentorshipsUsersCount = totals.size;

      // count of individual users in an active mentorship related to this mentorshipProgam, divided by count of Participants (from box to the left)
      const matchedPercent = participantsValue
        ? Math.round((mentorshipsUsersCount * 100) / participantsValue)
        : 0;

      return matchedPercent;
    }

    return 0;
  }, [participantsValue, mentorshipProgramData?.mentorshipProgram_by_pk]);

  const completedCheckins = useMemo(() => {
    // (count of check-ins from mentorshipProgramProgress related to this mentorshipProgram where completed = TRUE), divided by total count of check-ins from mentorshipProgramProgress related to this mentorshipProgram

    const mentorshipProgram = mentorshipProgramData?.mentorshipProgram_by_pk;

    if (mentorshipProgram?.mentorshipProgramModules) {
      const progresses = (
        mentorshipProgram.mentorshipProgramModules ?? []
      ).flatMap((m) => m.mentorshipProgramProgresses ?? []);

      const total = progresses.length;
      const completed = progresses.filter((p) => p.completed).length;
      const completion = total ? Math.round((completed * 100) / total) : 0;

      return completion;
    }

    return 0;
  }, [mentorshipProgramData?.mentorshipProgram_by_pk]);

  const relationshipsData = useMemo(() => {
    const mentorshipProgram = mentorshipProgramData?.mentorshipProgram_by_pk;

    const relationships: RelationshipsType[] = [];

    if (mentorshipProgram?.mentorships) {
      mentorshipProgram.mentorships.forEach((m) => {
        const total = m.mentorshipProgramProgresses.length;
        const completedCount = m.mentorshipProgramProgresses.filter(
          (p) => p.completed
        ).length;
        const completionPercent = total
          ? Math.round((completedCount * 100) / total)
          : 0;

        relationships.push({
          type: m.__typename,
          id: m.id,
          mentor: m.mentor,
          mentee: m.mentee,
          status: "MATCHED",
          matchDate: dayjs(m.createdAt).format("MM/DD/YYYY"),
          completionPercent,
        });
      });
    }

    if (mentorshipProgram?.userRelationshipRequests) {
      mentorshipProgram.userRelationshipRequests.forEach((m) => {
        let requesterIsMentor;
        if (m.requesterRole) {
          requesterIsMentor = m.requesterRole === "mentor" ? true : false;
        } else {
          requesterIsMentor = m.user.mentorEnabled;
        }

        relationships.push({
          type: m.__typename,
          id: m.id,
          mentor: requesterIsMentor ? m.user : m.userByRequestee,
          mentee: requesterIsMentor ? m.userByRequestee : m.user,
          status: "REQUESTED",
          matchDate: "",
          completionPercent: 0,
        });
      });
    }

    const filteredRelationships = relationships.filter((r) => {
      let passesSearchFilter = true;

      if (searchRelationshipsText.trim()) {
        const searchLower = searchRelationshipsText.toLowerCase().trim();
        const searchableFields = [
          r.mentor?.fullName || "",
          r.mentee?.fullName || "",
        ];

        passesSearchFilter = searchableFields.some((field) =>
          field.toLowerCase().includes(searchLower)
        );
      }

      return passesSearchFilter;
    });

    return filteredRelationships;
  }, [
    participantsValue,
    mentorshipProgramData?.mentorshipProgram_by_pk,
    searchRelationshipsText,
  ]);

  const participantsData = useMemo(() => {
    let source: Array<WorkspaceUsers>;

    if (
      programWorkspaceLocationId &&
      mentorshipParticipantsByWorkspaceLocationIdData?.workspaceUsers
    ) {
      source = [
        ...(mentorshipParticipantsByWorkspaceLocationIdData?.workspaceUsers as Array<WorkspaceUsers>),
      ];
    } else if (
      programWorkspaceId &&
      mentorshipParticipantsByWorkspaceIdData?.workspaceUsers
    ) {
      source = [
        ...(mentorshipParticipantsByWorkspaceIdData?.workspaceUsers as Array<WorkspaceUsers>),
      ];
    } else {
      source = [];
    }

    const filteredSource = source.filter((workspaceUser) => {
      const { user } = workspaceUser;

      let passesRoleFilter = true;

      if (mentorFilterEnabled && menteeFilterEnabled) {
        passesRoleFilter =
          user.mentorEnabled === true || user.menteeEnabled === true;
      } else if (mentorFilterEnabled && !menteeFilterEnabled) {
        passesRoleFilter = user.mentorEnabled === true;
      } else if (!mentorFilterEnabled && menteeFilterEnabled) {
        passesRoleFilter = user.menteeEnabled === true;
      }

      let passesSearchFilter = true;

      if (searchParticipantsText.trim()) {
        const searchLower = searchParticipantsText.toLowerCase().trim();
        const searchableFields = [
          user.fullName || "",
          user.email || "",
          user.userSpecialty?.name || "",
          user.workspaceLocation?.name || "",
        ];

        passesSearchFilter = searchableFields.some((field) =>
          field.toLowerCase().includes(searchLower)
        );
      }

      return passesRoleFilter && passesSearchFilter;
    });

    const data = filteredSource.map((workspaceUser) => {
      const { user } = workspaceUser;

      const userMentorships = [
        ...user.mentorshipsAsMentor,
        ...user.mentorshipsAsMentee,
      ];

      const userMentorshipsByProgramId = userMentorships.filter(
        (m) => m.mentorshipProgramId === Number(programId)
      );

      const progresses = (userMentorshipsByProgramId ?? []).flatMap(
        (m) => m.mentorshipProgramProgresses ?? []
      );

      const total = progresses.length;
      const completed = progresses.filter((p) => p.completed).length;

      return {
        id: user.id,
        name: user.fullName,
        profilePhotoLink: user.profilePhotoLink,
        mentorEnabled: user?.mentorEnabled,
        menteeEnabled: user?.menteeEnabled,
        specialty: user.userSpecialty?.name,
        location: user.workspaceLocation?.name,
        email: user.email,
        checkins: `${completed}/${total}`,
      };
    });

    return data;
  }, [
    programId,
    programWorkspaceId,
    programWorkspaceLocationId,
    mentorshipParticipantsByWorkspaceLocationIdData,
    mentorshipParticipantsByWorkspaceIdData,
    menteeFilterEnabled,
    mentorFilterEnabled,
    searchParticipantsText,
  ]);

  const surveyData = useMemo(() => {
    const mentorshipProgram = mentorshipProgramData?.mentorshipProgram_by_pk;

    console.log(
      "mentorshipProgram?.mentorshipSurveys: ",
      mentorshipProgram?.mentorshipSurveys
    );

    if (mentorshipProgram?.mentorshipSurveys) {
      const progresses = (mentorshipProgram?.mentorshipSurveys ?? []).flatMap(
        (m) => m.mentorshipSurveyProgress ?? []
      );

      const preSurveyProgresses = progresses.filter(
        (p) => p.surveyPrefix === "pre"
      );
      const preTotal = preSurveyProgresses.length;
      const preCompletedCount = preSurveyProgresses.filter(
        (p) => p.completed
      ).length;
      const preCompletionPercent = preTotal
        ? Math.round((preCompletedCount * 100) / preTotal)
        : 0;

      const midSurveyProgresses = progresses.filter(
        (p) => p.surveyPrefix === "mid"
      );
      const midTotal = midSurveyProgresses.length;
      const midCompletedCount = midSurveyProgresses.filter(
        (p) => p.completed
      ).length;
      const midCompletionPercent = midTotal
        ? Math.round((midCompletedCount * 100) / midTotal)
        : 0;

      const postSurveyProgresses = progresses.filter(
        (p) => p.surveyPrefix === "post"
      );
      const postTotal = postSurveyProgresses.length;
      const postCompletedCount = postSurveyProgresses.filter(
        (p) => p.completed
      ).length;
      const postCompletionPercent = postTotal
        ? Math.round((postCompletedCount * 100) / postTotal)
        : 0;

      return {
        preTotal,
        preCompletedCount,
        preCompletionPercent,
        midTotal,
        midCompletedCount,
        midCompletionPercent,
        postTotal,
        postCompletedCount,
        postCompletionPercent,
      };
    }

    return {
      preTotal: 0,
      preCompletedCount: 0,
      preCompletionPercent: 0,
      midTotal: 0,
      midCompletedCount: 0,
      midCompletionPercent: 0,
      postTotal: 0,
      postCompletedCount: 0,
      postCompletionPercent: 0,
    };
  }, [participantsValue, mentorshipProgramData?.mentorshipProgram_by_pk]);

  return (
    <Container component="main" maxWidth={false}>
      <div className="mentorship-details-title-row">
        <h1 className="screen-title">
          {mentorshipProgramData?.mentorshipProgram_by_pk?.name ??
            "Program Name"}
        </h1>

        <div className="mentorship-details-edit-button">
          <img
            src={process.env.PUBLIC_URL + "/assets/images/edit-pencil.svg"}
            alt="remove module icon"
            className="mentorship-details-edit-icon"
            onClick={() => {
              const path = `${routeNames.MENTORSHIP_EDIT.replace(
                ":programId",
                programId
              )}`;

              navigate(path);
            }}
          />
        </div>
      </div>

      <div className="mentorship-details-title-row">
        <div className="mentorship-details-total-item">
          <img
            src={process.env.PUBLIC_URL + "/assets/images/rotate-clockwise.svg"}
            alt="rotate clockwise icon"
            className="mentorship-details-total-icon"
          />
          <div>
            <span className="mentorship-details-total-title">
              {"PARTICIPANTS"}
            </span>
            <div className="mentorship-details-total-info">
              <span className="mentorship-details-total-value">
                {participantsValue}
              </span>
              <span className="total-description">Eligible</span>
            </div>
          </div>
        </div>

        <div className="mentorship-details-total-item">
          <img
            src={process.env.PUBLIC_URL + "/assets/images/purple-bg-arrow.svg"}
            alt="rotate clockwise icon"
            className="mentorship-details-total-icon"
          />
          <div>
            <span
              className="mentorship-details-total-title"
              style={{ color: "#BF47FD" }}
            >
              {"MATCHED"}
            </span>
            <div className="mentorship-details-total-info">
              <span className="mentorship-details-total-value">{`${matchedValue}%`}</span>
            </div>
          </div>
        </div>

        <div className="mentorship-details-total-item">
          <img
            src={process.env.PUBLIC_URL + "/assets/images/mood-check.svg"}
            alt="rotate clockwise icon"
            className="mentorship-details-total-icon"
          />
          <div>
            <span
              className="mentorship-details-total-title"
              style={{ color: "#FB779C" }}
            >
              {"ON TIME CHECK-INS"}
            </span>
            <div className="mentorship-details-total-info">
              <span className="mentorship-details-total-value">
                {`${completedCheckins}%`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Collapse
        expandIconPosition="end"
        className="mentorship-details-collapse"
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) => {
          if (isActive) {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-up-arrow.svg"
                }
                alt="collapse-up-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          } else {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-down-arrow.svg"
                }
                alt="collapse-down-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          }
        }}
        items={[
          {
            key: `curriculum-overview-content`,
            label: (
              <div className="mentorship-details-collapse-row">
                <span className="mentorship-details-collapse-label">
                  {"Program Summary"}
                </span>
                <span className="mentorship-details-collapse-label completed">
                  {`${programCompletedCount} completed (${programCompletedPercent}%)`}
                </span>
                <Progress
                  percent={programCompletedPercent}
                  showInfo={false}
                  strokeColor={
                    "linear-gradient(108deg, #FB7798 10.92%, #EC76FF 164.77%)"
                  }
                  trailColor={"#FFE0EE"}
                  className="mentorship-details-collapse-progress"
                />
              </div>
            ),
            children: (
              <div className="mentorship-details-collapse-content">
                <span className="mentorship-details-module-list">
                  <strong>{"Availability"}</strong>
                  {mentorshipProgramData?.mentorshipProgram_by_pk
                    ?.workspaceLocation?.name
                    ? mentorshipProgramData?.mentorshipProgram_by_pk
                        ?.workspaceLocation?.name
                    : "Organization-wide"}
                </span>
                <span className="mentorship-details-module-list">
                  <strong>{"Matching"}</strong>
                  {mentorshipProgramData?.mentorshipProgram_by_pk
                    ?.matchingMethod === "adminManaged"
                    ? "Admin Managed"
                    : "User Requested"}
                </span>
                {mentorshipProgramData?.mentorshipProgram_by_pk?.mentorshipProgramModules.map(
                  (module) => {
                    return (
                      <span
                        className="mentorship-details-module-list"
                        key={module.id}
                      >
                        <strong>{`Check-In ${module.order} `}</strong>
                        {module.name}
                      </span>
                    );
                  }
                )}
              </div>
            ),
          },
        ]}
        onChange={(key: string | string[]) => {
          // console.log("Collapse key: ", key);
        }}
      />

      {mentorshipProgramData?.mentorshipProgram_by_pk?.mentorshipProgramModules.map(
        (module: MentorshipProgramModule) => {
          let progressesCompletedCount = 0;
          let progressesCompletedPercent = 0;
          if (module.mentorshipProgramProgresses) {
            const total = module.mentorshipProgramProgresses.length;
            const completed = module.mentorshipProgramProgresses.filter(
              (p) => p.completed
            ).length;

            progressesCompletedCount = completed;

            progressesCompletedPercent = total
              ? Math.round((completed * 100) / total)
              : 0;
          }

          return (
            <Collapse
              key={module.id}
              expandIconPosition="end"
              className="mentorship-details-collapse"
              defaultActiveKey={["1"]}
              expandIcon={({ isActive }) => {
                if (isActive) {
                  return (
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/assets/images/collapse-up-arrow.svg"
                      }
                      alt="collapse-up-arrow"
                      className="mentorship-details-collapse-arrow"
                    />
                  );
                } else {
                  return (
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/assets/images/collapse-down-arrow.svg"
                      }
                      alt="collapse-down-arrow"
                      className="mentorship-details-collapse-arrow"
                    />
                  );
                }
              }}
              items={[
                {
                  key: `module-content-${module.id}`,
                  label: (
                    <div className="mentorship-details-collapse-row">
                      <span className="mentorship-details-collapse-label">
                        {`Check-In ${module.order}`}
                      </span>
                      <span className="mentorship-details-collapse-label completed">
                        {`${progressesCompletedCount} completed (${progressesCompletedPercent}%)`}
                      </span>
                      <Progress
                        percent={progressesCompletedPercent}
                        showInfo={false}
                        strokeColor={
                          "linear-gradient(108deg, #FB7798 10.92%, #EC76FF 164.77%)"
                        }
                        trailColor={"#FFE0EE"}
                        className="mentorship-details-collapse-progress"
                      />
                    </div>
                  ),
                  children: (
                    <div className="mentorship-details-collapse-content">
                      <span className="mentorship-details-module-data">
                        <strong>{"Duration: "}</strong>
                        {`This Check-In begins ${module.duration} days after the previous check-in.`}
                      </span>
                      <span className="mentorship-details-module-data">
                        <strong>{"Topic: "}</strong>
                        {`${module.name}`}
                      </span>
                      <span className="mentorship-details-module-data">
                        <strong>{"Talking Points: "}</strong>
                        {`${module.content}`}
                      </span>
                      <div className="mentorship-row">
                        <Table<any>
                          columns={columns}
                          pagination={{
                            position: ["bottomCenter"],
                            pageSizeOptions: [10, 20],
                          }}
                          dataSource={module?.mentorshipProgramProgresses}
                          loading={mentorshipProgramLoading}
                          className="mentorship-details-table"
                          rowKey={(record) => record.id}
                          onRow={(record, rowIndex) => {
                            return {
                              onClick: (event) => {
                                const path = `${routeNames.MENTORSHIP_DETAILS.replace(
                                  ":programId",
                                  `${record.id}`
                                )}`;

                                navigate(path);
                              },
                            };
                          }}
                        />
                      </div>
                    </div>
                  ),
                },
              ]}
              onChange={(key: string | string[]) => {
                // console.log("Collapse key: ", key);
              }}
            />
          );
        }
      )}

      <Collapse
        expandIconPosition="end"
        className="mentorship-details-collapse"
        activeKey={activeKeys}
        onChange={(key) => setActiveKeys(key as string[])}
        expandIcon={({ isActive }) => {
          if (isActive) {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-up-arrow.svg"
                }
                alt="collapse-up-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          } else {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-down-arrow.svg"
                }
                alt="collapse-down-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          }
        }}
        items={[
          {
            key: `relationship-content`,
            label: (
              <div className="mentorship-details-collapse-row with-button">
                <span className="mentorship-details-collapse-label no-progress">
                  {"Relationships"}
                </span>
                {programMatchingMethod === MatchingMethod.AdminManaged ? (
                  <div className="mentorship-details-edit-button">
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/assets/images/edit-pencil.svg"
                      }
                      alt="remove module icon"
                      className="mentorship-details-edit-icon"
                      onClick={() => {
                        const path = `${routeNames.MENTORSHIP_RELATIONSHIP_ADD.replace(
                          ":programId",
                          programId
                        )}`;

                        navigate(path);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ),
            children: (
              <div className="mentorship-details-collapse-content">
                <Input
                  prefix={
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/images/searchbar-icon.svg`}
                      alt="Search"
                      className="search-prefix"
                    />
                  }
                  placeholder="Search..."
                  allowClear
                  onChange={(e) => setSearchRelationshipsText(e.target.value)}
                  className="search-input"
                />
                <div className="mentorship-row">
                  <Table<RelationshipsType>
                    columns={relationshipsColumns}
                    pagination={{
                      position: ["bottomCenter"],
                      pageSizeOptions: [10, 20],
                    }}
                    dataSource={relationshipsData}
                    loading={mentorshipProgramLoading}
                    className="mentorship-details-table"
                    rowKey={(record) => record.id}
                  />
                </div>
              </div>
            ),
          },
        ]}
      />

      <Collapse
        expandIconPosition="end"
        className="mentorship-details-collapse"
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) => {
          if (isActive) {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-up-arrow.svg"
                }
                alt="collapse-up-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          } else {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-down-arrow.svg"
                }
                alt="collapse-down-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          }
        }}
        items={[
          {
            key: `curriculum-overview-content`,
            label: (
              <div className="mentorship-details-collapse-row">
                <span className="mentorship-details-collapse-label">
                  {"Eligible Participants"}
                </span>
              </div>
            ),
            children: (
              <div className="mentorship-details-collapse-content">
                <div className="mentor-filter-row">
                  <div
                    className="mentor-filter-button"
                    onClick={() => {
                      setMentorFilterEnabled((prev) => !prev);
                    }}
                    style={
                      mentorFilterEnabled ? { border: "1px solid #e6e6e6" } : {}
                    }
                  >
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/assets/images/mentor-filter.svg"
                      }
                      alt="mentor-filter"
                      className="mentor-filter-img"
                    />
                    <span className="mentor-filter-label">
                      {mentorLabel || "Mentor"}
                    </span>
                  </div>

                  <div
                    className="mentor-filter-button"
                    onClick={() => {
                      setMenteeFilterEnabled((prev) => !prev);
                    }}
                    style={
                      menteeFilterEnabled ? { border: "1px solid #e6e6e6" } : {}
                    }
                  >
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/assets/images/mentee-filter.svg"
                      }
                      alt="mentee-filter"
                      className="mentor-filter-img"
                    />
                    <span className="mentor-filter-label">
                      {menteeLabel || "Mentee"}
                    </span>
                  </div>
                </div>
                <Input
                  prefix={
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/images/searchbar-icon.svg`}
                      alt="Search"
                      className="search-prefix"
                    />
                  }
                  placeholder="Search..."
                  allowClear
                  onChange={(e) => setSearchParticipantsText(e.target.value)}
                  className="search-input"
                />
                <div className="mentorship-row">
                  <Table<ParticipantsType>
                    columns={participantsColumns}
                    pagination={{
                      position: ["bottomCenter"],
                      pageSizeOptions: [10, 20],
                    }}
                    dataSource={participantsData}
                    loading={mentorshipProgramLoading}
                    className="mentorship-details-table"
                    rowKey={(record) => record.id}
                  />
                </div>
              </div>
            ),
          },
        ]}
        onChange={(key: string | string[]) => {
          // console.log("Collapse key: ", key);
        }}
      />

      <Collapse
        expandIconPosition="end"
        className="mentorship-details-collapse"
        defaultActiveKey={["1"]}
        expandIcon={({ isActive }) => {
          if (isActive) {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-up-arrow.svg"
                }
                alt="collapse-up-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          } else {
            return (
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/collapse-down-arrow.svg"
                }
                alt="collapse-down-arrow"
                className="mentorship-details-collapse-arrow"
              />
            );
          }
        }}
        items={[
          {
            key: `curriculum-overview-content`,
            label: (
              <div className="mentorship-details-collapse-row">
                <span className="mentorship-details-collapse-label">
                  {"Survey Completion"}
                </span>
              </div>
            ),
            children: (
              <div className="mentorship-details-collapse-content">
                <div className="survey-row main">
                  <div className="survey-row">
                    <div className="survey-column">
                      <span className="survey-prefix-text">{"Pre"}</span>
                      <span className="survey-value">{`${surveyData.preCompletionPercent}%`}</span>
                    </div>
                    <div className="survey-column">
                      <Progress
                        percent={surveyData.preCompletionPercent}
                        showInfo={false}
                        strokeColor={
                          "linear-gradient(108deg, #FB7798 10.92%, #EC76FF 164.77%)"
                        }
                        trailColor={"#DBD8D8"}
                        className="survey-progress"
                      />
                    </div>
                    <div className="survey-column">
                      {/* <span className="survey-prefix-text">{"Total"}</span> */}
                      <span className="survey-value">{`${surveyData.preCompletedCount}/${surveyData.preTotal}`}</span>
                    </div>
                  </div>

                  <div className="survey-row">
                    <div className="survey-column">
                      <span className="survey-prefix-text">{"Mid"}</span>
                      <span className="survey-value">{`${surveyData.midCompletionPercent}%`}</span>
                    </div>
                    <div className="survey-column">
                      <Progress
                        percent={surveyData.midCompletionPercent}
                        showInfo={false}
                        strokeColor={
                          "linear-gradient(108deg, #71C4E6 10.92%, #748AEC 164.77%)"
                        }
                        trailColor={"#DBD8D8"}
                        className="survey-progress"
                      />
                    </div>
                    <div className="survey-column">
                      {/* <span className="survey-prefix-text">{"Total"}</span> */}
                      <span className="survey-value">{`${surveyData.midCompletedCount}/${surveyData.midTotal}`}</span>
                    </div>
                  </div>

                  <div className="survey-row">
                    <div className="survey-column">
                      <span className="survey-prefix-text">{"Post"}</span>
                      <span className="survey-value">{`${surveyData.postCompletionPercent}%`}</span>
                    </div>
                    <div className="survey-column">
                      <Progress
                        percent={surveyData.postCompletionPercent}
                        showInfo={false}
                        strokeColor={
                          "linear-gradient(108deg, #BF47FD 10.92%, #7A00F8 164.77%)"
                        }
                        trailColor={"#DBD8D8"}
                        className="survey-progress"
                      />
                    </div>
                    <div className="survey-column">
                      {/* <span className="survey-prefix-text">{"Total"}</span> */}
                      <span className="survey-value">{`${surveyData.postCompletedCount}/${surveyData.postTotal}`}</span>
                    </div>
                  </div>
                </div>
              </div>
            ),
          },
        ]}
        onChange={(key: string | string[]) => {
          // console.log("Collapse key: ", key);
        }}
      />
    </Container>
  );
}
